/**
 * Retired: the single encrypted list deletes used to live in.
 *
 * It grew with every delete, inside one event NIP-44 caps at 64KB. Deletes are
 * now withdrawals the relay acts on — `withdrawn.ts`. This list is read once per
 * account so its ids can be withdrawn too, and is never written again.
 *
 * What it was:
 *
 * Nostr has no true delete. A gift wrap is signed by a throwaway key nobody
 * keeps, so not even the account that reads it can withdraw it from the relay:
 * the ciphertext stays, unreadable to everyone but this account. Delete is
 * therefore a statement this client makes and keeps — the wrap id goes on a
 * list, the plaintext is removed from the local index, and no sync opens that
 * wrap again.
 *
 * The list lives in an addressable event encrypted to this account alone. The
 * contact list is shared with postrd, so it uses the conversation key between
 * the owner and the ingest identity. Nothing server-side reads this one, so it
 * is encrypted to the owner's own pubkey.
 */
import type { Event as NostrEvent } from "nostr-tools";
import { gzipSync, gunzipSync } from "fflate";
import type { RelayConnection } from "./relay";
import type { MailSigner } from "./signer";
import { KIND_APP_DATA, decodeBody, toBase64 } from "./contacts";

export const IDENTIFIER = "postr/deleted";

export interface DeletedList {
  version: number;
  updated: string;
  /** Gift wrap ids. The wrap id is the only name every message is sure to have. */
  ids: string[];
}

export interface DeletedRecord {
  list: DeletedList;
  /** When the event was written, so the next read can skip an unchanged list. */
  createdAt: number;
}

/**
 * Read the list, and only when it has changed.
 *
 * `newerThan` is the `createdAt` of the last read. A decrypt is a round-trip to
 * the phone for a remote signer, and the poller runs every minute, so reading a
 * list that nobody has rewritten costs an approval for nothing.
 */
export async function fetchDeleted(
  relay: RelayConnection,
  ownerPubkey: string,
  signer: MailSigner,
  newerThan = 0,
): Promise<DeletedRecord | null> {
  const events = await relay.list({
    kinds: [KIND_APP_DATA],
    authors: [ownerPubkey],
    "#d": [IDENTIFIER],
    limit: 1,
  });
  const newest = events.sort((a, b) => b.created_at - a.created_at)[0];
  if (!newest || newest.created_at <= newerThan) return null;

  const plaintext = await signer.nip44Decrypt(ownerPubkey, newest.content);
  const json = new TextDecoder().decode(gunzipSync(decodeBody(plaintext)));
  return { list: JSON.parse(json) as DeletedList, createdAt: newest.created_at };
}

/**
 * Publish a new version.
 *
 * Ids are gzipped for the same reason addresses are: 64 hex characters each
 * reaches NIP-44's ceiling at a few hundred of them, and they compress well.
 */
export async function publishDeleted(
  relay: RelayConnection,
  signer: MailSigner,
  ids: readonly string[],
  version: number,
  /**
   * The newest list's timestamp. A replaceable event that shares a second with
   * the one it replaces may lose to it, so each list is stamped after it.
   */
  after = 0,
): Promise<NostrEvent> {
  const list: DeletedList = {
    version,
    updated: new Date().toISOString(),
    ids: [...new Set(ids.map((id) => id.trim().toLowerCase()).filter(Boolean))].sort(),
  };
  const squeezed = gzipSync(new TextEncoder().encode(JSON.stringify(list)));
  const content = await signer.nip44Encrypt(signer.pubkey, toBase64(squeezed));

  const event = await signer.signEvent({
    kind: KIND_APP_DATA,
    created_at: Math.max(Math.floor(Date.now() / 1000), after + 1),
    tags: [["d", IDENTIFIER]],
    content,
  });
  await relay.publish(event);
  return event;
}
